import { debounce, mediaQueryLarge } from "@theme/utilities";
import { Component } from "@theme/component";
import { sectionRenderer } from "@theme/section-renderer";
import { CollectionUpdateEvent, SearchUpdateEvent, StandardEvents } from "@shopify/events";
import { convertMoneyToMinorUnits, formatMoney } from "@theme/money-formatting";
import { runProductGridMotion } from "@theme/vp-plp-grid-motion";
import { augmentFacetFormData, ensurePlpVehicleFiltersFromSession, findDesktopFacetsFormComponent, resolveFacetFormData, syncUrlOnlyModelFilterPills } from "@theme/facet-form-data";
//#region assets/facets.ts
/**
* Search query parameter.
* @type {string}
*/
var SEARCH_QUERY = "q";
/**
* Handles the main facets form functionality
*
* @typedef {Object} FacetsFormRefs
* @property {HTMLFormElement} facetsForm - The main facets form element
* @property {HTMLElement | undefined} facetStatus - The facet status element
*
* @extends {Component<FacetsFormRefs>}
*/
var FacetsFormComponent = class extends Component {
	requiredRefs = ["facetsForm"];
	connectedCallback() {
		super.connectedCallback();
		queueMicrotask(() => {
			syncUrlOnlyModelFilterPills();
			ensurePlpVehicleFiltersFromSession();
		});
		if (this.dataset.filterStyle === "vertical") mediaQueryLarge.addEventListener("change", this.#onBreakpointChange);
	}
	disconnectedCallback() {
		super.disconnectedCallback();
		mediaQueryLarge.removeEventListener("change", this.#onBreakpointChange);
	}
	#onBreakpointChange = (event) => {
		if (!event.matches) return;
		const drawer = document.getElementById("filters-drawer");
		if (drawer && "close" in drawer && typeof drawer.close === "function") drawer.close();
	};
	/**
	* Creates URL parameters from form data
	* @param {FormData} [formData] - Optional form data to use instead of the main form
	* @returns {URLSearchParams} The processed URL parameters
	*/
	createURLParameters(formData = resolveFacetFormData(this.refs.facetsForm, this.closest(".shopify-section"), this.sectionId)) {
		let newParameters = new URLSearchParams(augmentFacetFormData(formData));
		if (newParameters.get("filter.v.price.gte") === "") newParameters.delete("filter.v.price.gte");
		if (newParameters.get("filter.v.price.lte") === "") newParameters.delete("filter.v.price.lte");
		newParameters.delete("page");
		const searchQuery = this.#getSearchQuery();
		if (searchQuery) newParameters.set(SEARCH_QUERY, searchQuery);
		return newParameters;
	}
	/**
	* Gets the search query parameter from the current URL
	* @returns {string} The search query
	*/
	#getSearchQuery() {
		return new URL(window.location.href).searchParams.get(SEARCH_QUERY) ?? "";
	}
	get sectionId() {
		const id = this.getAttribute("section-id");
		if (!id) throw new Error("Section ID is required");
		return id;
	}
	/**
	* Updates the URL hash with current filter parameters
	*/
	#updateURLHash() {
		const url = new URL(window.location.href);
		const urlParameters = this.createURLParameters();
		url.search = "";
		for (const [param, value] of urlParameters.entries()) url.searchParams.append(param, value);
		url.searchParams.delete("preview_theme_id");
		history.pushState({ urlParameters: urlParameters.toString() }, "", url.toString());
	}
	/**
	* Updates filters and renders the section
	*/
	updateFilters = () => {
		this.#updateURLHash();
		const renderPromise = this.#updateSection();
		renderPromise.finally(() => {
			syncUrlOnlyModelFilterPills();
		});
		if (this.dataset.pageType === "search") this.#dispatchSearchUpdateEvent(renderPromise);
		else this.#dispatchCollectionUpdateEvent(renderPromise);
	};
	/**
	* Dispatches search:update event if on the search page.
	* @param {Promise<void>} renderPromise - The promise from section rendering
	*/
	#dispatchSearchUpdateEvent(renderPromise) {
		const query = this.#getSearchQuery();
		const deferredPromise = SearchUpdateEvent.createPromise();
		const productFilters = SearchUpdateEvent.parseProductFilters();
		this.dispatchEvent(new SearchUpdateEvent({
			search: {
				query,
				...productFilters && { productFilters },
				sortKey: SearchUpdateEvent.getSortKey()
			},
			promise: deferredPromise.promise
		}));
		renderPromise.then(() => deferredPromise.resolve({ totalCount: this.#getResultsCount() })).catch((error) => deferredPromise.reject(error));
	}
	/**
	* Gets the count of product results from the server-rendered data attribute.
	* @returns {number}
	*/
	#getResultsCount() {
		return Number(this.dataset.resultsCount) || 0;
	}
	/**
	* Updates filters based on a provided URL
	* @param {string} url - The URL to update filters with
	* @param {{ motion?: boolean }} [options] - Boot restore passes `{ motion: false }`
	*   so session vehicle sync does not fade the grid or full-reload the page.
	* @returns {Promise<void>}
	*/
	updateFiltersByURL(url, { motion = true } = {}) {
		let next = url;
		try {
			const parsed = new URL(url, window.location.origin);
			parsed.searchParams.delete("preview_theme_id");
			next = `${parsed.pathname}${parsed.search}${parsed.hash}`;
		} catch {}
		history.pushState("", "", next);
		const renderPromise = this.#updateSection({ motion });
		renderPromise.finally(() => {
			syncUrlOnlyModelFilterPills();
		});
		if (this.dataset.pageType === "search") this.#dispatchSearchUpdateEvent(renderPromise);
		else this.#dispatchCollectionUpdateEvent(renderPromise);
		return renderPromise;
	}
	/**
	* Updates the section with a fade-out-up / fade-in-down card sequence.
	* @param {{ motion?: boolean }} [options]
	* @returns {Promise<void>}
	*/
	#updateSection({ motion = true } = {}) {
		return runProductGridMotion(() => sectionRenderer.renderSection(this.sectionId), { animate: motion });
	}
	/**
	* Dispatches collection:update event if on a collection page.
	* @param {Promise<void>} renderPromise - The promise from section rendering
	*/
	#dispatchCollectionUpdateEvent(renderPromise) {
		/** @type {any} */
		const rawId = this.dataset.collectionId;
		const collectionData = {
			id: rawId && /^\d+$/.test(rawId) ? rawId : null,
			handle: this.dataset.collectionHandle,
			productsCount: this.#getResultsCount()
		};
		const deferredPromise = CollectionUpdateEvent.createPromise();
		const productFilters = CollectionUpdateEvent.parseProductFilters();
		this.dispatchEvent(new CollectionUpdateEvent({
			collection: collectionData,
			...productFilters && { productFilters },
			sortKey: CollectionUpdateEvent.getSortKey(),
			promise: deferredPromise.promise
		}));
		renderPromise.then(() => deferredPromise.resolve({ productsCount: this.#getResultsCount() })).catch((error) => deferredPromise.reject(error));
	}
};
if (!customElements.get("facets-form-component")) customElements.define("facets-form-component", FacetsFormComponent);
/**
* @typedef {Object} FacetInputsRefs
* @property {HTMLInputElement[]} facetInputs - The facet input elements
*/
/**
* Handles individual facet input functionality
* @extends {Component<FacetInputsRefs>}
*/
var FacetInputsComponent = class extends Component {
	get sectionId() {
		const id = this.closest(".shopify-section")?.id;
		if (!id) throw new Error("FacetInputs component must be a child of a section");
		return id;
	}
	/**
	* Updates filters and the selected facet summary
	*/
	updateFilters() {
		const facetsForm = this.closest("facets-form-component");
		if (!(facetsForm instanceof FacetsFormComponent)) return;
		facetsForm.updateFilters();
		this.#updateSelectedFacetSummary();
	}
	/**
	* Handles keydown events for the facets form
	* @param {KeyboardEvent} event - The keydown event
	*/
	handleKeyDown(event) {
		if (!(event.target instanceof HTMLElement)) return;
		const closestInput = event.target.querySelector("input");
		if (!(closestInput instanceof HTMLInputElement)) return;
		if (event.key === "Enter" || event.key === " ") {
			event.preventDefault();
			closestInput.checked = !closestInput.checked;
			this.updateFilters();
		}
	}
	/**
	* Fires immediately on pointerdown so a quick tap triggers the prefetch.
	* @param {PointerEvent} event
	*/
	prefetchPageImmediate(event) {
		if (!(event.target instanceof HTMLElement)) return;
		this.#prefetchOption(event.target);
	}
	/**
	* Reads `checked` at call time, so callers must invoke it before the toggle
	* for the predicted post-toggle URL to be correct.
	* @param {HTMLElement} optionElement
	*/
	#prefetchOption(optionElement) {
		const form = this.closest("form");
		if (!form) return;
		const inputElement = optionElement.querySelector("input");
		if (!(inputElement instanceof HTMLInputElement) || inputElement.disabled) return;
		const facetsForm = this.closest("facets-form-component");
		if (!(facetsForm instanceof FacetsFormComponent)) return;
		const formData = new FormData(form);
		if (!inputElement.checked) formData.append(inputElement.name, inputElement.value);
		const urlParameters = facetsForm.createURLParameters(formData);
		const url = new URL(window.location.pathname, window.location.origin);
		for (const [key, value] of urlParameters) url.searchParams.append(key, value);
		if (inputElement.checked) url.searchParams.delete(inputElement.name, inputElement.value);
		sectionRenderer.getSectionHTML(this.sectionId, true, url);
	}
	/**
	* @param {PointerEvent} event
	*/
	prefetchPage = debounce((event) => {
		if (!(event.target instanceof HTMLElement)) return;
		this.#prefetchOption(event.target);
	}, 200);
	cancelPrefetchPage = () => this.prefetchPage.cancel();
	/**
	* Updates the selected facet summary
	*/
	#updateSelectedFacetSummary() {
		if (!this.refs.facetInputs) return;
		const checkedInputElements = this.refs.facetInputs.filter((input) => input.checked);
		const statusComponent = this.closest("details")?.querySelector("facet-status-component");
		if (!(statusComponent instanceof FacetStatusComponent)) return;
		statusComponent.updateListSummary(checkedInputElements);
	}
};
if (!customElements.get("facet-inputs-component")) customElements.define("facet-inputs-component", FacetInputsComponent);
/**
* @typedef {Object} PriceFacetRefs
* @property {HTMLInputElement} minInput - The minimum price input
* @property {HTMLInputElement} maxInput - The maximum price input
*/
/**
* Handles price facet functionality
* @extends {Component<PriceFacetRefs>}
*/
var PriceFacetComponent = class extends Component {
	/** @type {string} */
	currency;
	/** @type {string} */
	moneyFormat;
	connectedCallback() {
		super.connectedCallback();
		this.addEventListener("keydown", this.#onKeyDown);
		this.currency = this.dataset.currency ?? "USD";
		this.moneyFormat = this.#extractMoneyPlaceholder(this.dataset.moneyFormat ?? "{{amount}}");
	}
	disconnectedCallback() {
		super.disconnectedCallback();
		this.removeEventListener("keydown", this.#onKeyDown);
	}
	/**
	* Extracts the placeholder from a money format string, removing currency symbols.
	* @param {string} format - The money format (e.g., "${{amount}}", "{{amount}} USD")
	* @returns {string} Just the placeholder (e.g., "{{amount}}")
	*/
	#extractMoneyPlaceholder(format) {
		const match = format.match(/{{\s*\w+\s*}}/);
		return match ? match[0] : "{{amount}}";
	}
	/**
	* Handles keydown events to restrict input to valid characters
	* @param {KeyboardEvent} event - The keydown event
	*/
	#onKeyDown = (event) => {
		if (event.metaKey) return;
		if (!event.key.match(/[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/)) event.preventDefault();
	};
	/**
	* Updates price filter and results
	*/
	updatePriceFilterAndResults() {
		const { minInput, maxInput } = this.refs;
		this.#adjustToValidValues(minInput);
		this.#adjustToValidValues(maxInput);
		const facetsForm = this.closest("facets-form-component");
		if (!(facetsForm instanceof FacetsFormComponent)) return;
		facetsForm.updateFilters();
		this.#setMinAndMaxValues();
		this.#updateSummary();
	}
	/**
	* Parses a formatted money value into minor units
	* displayValue can come from user input or API response
	* @param {string} displayValue - The display value (e.g., "10.50" for USD, "9,50" for EUR, "1000" for JPY)
	* @param {string} currency - The currency code
	* @returns {number} The value in minor units
	*/
	#parseDisplayValue(displayValue, currency) {
		return convertMoneyToMinorUnits(displayValue, currency) ?? 0;
	}
	/**
	* Adjusts input values to be within valid range
	* @param {HTMLInputElement} input - The input element to adjust
	*/
	#adjustToValidValues(input) {
		if (input.value.trim() === "") return;
		const { currency, moneyFormat } = this;
		const value = this.#parseDisplayValue(input.value, currency);
		const min = this.#parseDisplayValue(input.getAttribute("data-min") ?? "0", currency);
		const max = this.#parseDisplayValue(input.getAttribute("data-max") ?? "0", currency);
		if (value < min) input.value = formatMoney(min, moneyFormat, currency);
		else if (value > max) input.value = formatMoney(max, moneyFormat, currency);
	}
	/**
	* Sets min and max values for the inputs
	*/
	#setMinAndMaxValues() {
		const { minInput, maxInput } = this.refs;
		if (maxInput.value) minInput.setAttribute("data-max", maxInput.value);
		if (minInput.value) maxInput.setAttribute("data-min", minInput.value);
		if (minInput.value === "") maxInput.setAttribute("data-min", "0");
		if (maxInput.value === "") minInput.setAttribute("data-max", maxInput.getAttribute("data-max") ?? "");
	}
	/**
	* Updates the price summary
	*/
	#updateSummary() {
		const { minInput, maxInput } = this.refs;
		const statusComponent = this.closest("details")?.querySelector("facet-status-component");
		if (!(statusComponent instanceof FacetStatusComponent)) return;
		statusComponent?.updatePriceSummary(minInput, maxInput);
	}
};
if (!customElements.get("price-facet-component")) customElements.define("price-facet-component", PriceFacetComponent);
/**
* Handles clearing of facet filters
* @extends {Component}
*/
var FacetClearComponent = class extends Component {
	requiredRefs = ["clearButton"];
	connectedCallback() {
		super.connectedCallback();
		this.addEventListener("keyup", this.#handleKeyUp);
		document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
		document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
	}
	disconnectedCallback() {
		super.disconnectedCallback();
		document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
		document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
	}
	/**
	* Clears the filter
	* @param {Event} event - The click event
	*/
	clearFilter(event) {
		if (!(event.target instanceof HTMLElement)) return;
		if (event instanceof KeyboardEvent) {
			if (event.key !== "Enter" && event.key !== " ") return;
			event.preventDefault();
		}
		event.target.closest("facet-inputs-component, price-facet-component")?.querySelectorAll("[type=\"checkbox\"]:checked, input").forEach((input) => {
			if (input instanceof HTMLInputElement) {
				input.checked = false;
				input.value = "";
			}
		});
		const statusComponent = event.target.closest("details")?.querySelector("facet-status-component");
		if (!(statusComponent instanceof FacetStatusComponent)) return;
		statusComponent.clearSummary();
		const facetsForm = this.closest("facets-form-component");
		if (!(facetsForm instanceof FacetsFormComponent)) return;
		facetsForm.updateFilters();
	}
	/**
	* Handles keyup events
	* @param {KeyboardEvent} event - The keyup event
	*/
	#handleKeyUp = (event) => {
		if (event.metaKey) return;
		if (event.key === "Enter") this.clearFilter(event);
	};
	/**
	* Toggle clear button visibility when filters are applied. Happens before the
	* Section Rendering Request resolves.
	*
	* @param {SearchUpdateEvent | CollectionUpdateEvent} event
	*/
	#handleFilterUpdate = (event) => {
		const eventSection = event.target?.closest(".shopify-section");
		const mySection = this.closest(".shopify-section");
		if (eventSection && mySection && eventSection !== mySection) return;
		const { clearButton } = this.refs;
		if (clearButton instanceof Element) {
			const filters = event instanceof SearchUpdateEvent ? event.search?.productFilters ?? [] : event.productFilters ?? [];
			clearButton.classList.toggle("facets__clear--active", filters.length > 0);
		}
	};
};
if (!customElements.get("facet-clear-component")) customElements.define("facet-clear-component", FacetClearComponent);
/**
* @typedef {Object} FacetRemoveComponentRefs
* @property {HTMLInputElement | undefined} clearButton - The button to clear filters
*/
/**
* Handles removal of individual facet filters
* @extends {Component<FacetRemoveComponentRefs>}
*/
var FacetRemoveComponent = class extends Component {
	connectedCallback() {
		super.connectedCallback();
		document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
		document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
	}
	disconnectedCallback() {
		super.disconnectedCallback();
		document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
		document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
	}
	/**
	* Removes the filter
	* @param {Object} data - The data object
	* @param {string} data.form - The form to remove the filter from
	* @param {Event} event - The click event
	*/
	removeFilter({ form }, event) {
		if (event instanceof KeyboardEvent) {
			if (event.key !== "Enter" && event.key !== " ") return;
			event.preventDefault();
		}
		const url = this.dataset.url;
		if (!url) return;
		const facetsForm = form ? document.getElementById(form) : this.closest("facets-form-component");
		if (!(facetsForm instanceof FacetsFormComponent)) return;
		facetsForm.updateFiltersByURL(url);
	}
	/**
	* Toggle clear button visibility when filters are applied. Happens before the
	* Section Rendering Request resolves.
	*
	* @param {SearchUpdateEvent | CollectionUpdateEvent} event
	*/
	#handleFilterUpdate = (event) => {
		const eventSection = event.target?.closest(".shopify-section");
		const mySection = this.closest(".shopify-section");
		if (eventSection && mySection && eventSection !== mySection) return;
		const { clearButton } = this.refs;
		if (clearButton instanceof Element) {
			const activeClass = this.getAttribute("active-class") || "active";
			const filters = event instanceof SearchUpdateEvent ? event.search?.productFilters ?? [] : event.productFilters ?? [];
			clearButton.classList.toggle(activeClass, filters.length > 0);
		}
	};
};
if (!customElements.get("facet-remove-component")) customElements.define("facet-remove-component", FacetRemoveComponent);
/**
* Handles sorting filter functionality
*
* @typedef {Object} SortingFilterRefs
* @property {HTMLDetailsElement} details - The details element
* @property {HTMLElement} summary - The summary element
* @property {HTMLElement} listbox - The listbox element
*
* @extends {Component}
*/
var SortingFilterComponent = class extends Component {
	requiredRefs = [
		"details",
		"summary",
		"listbox"
	];
	/**
	* Handles keyboard navigation in the sorting dropdown
	* @param {KeyboardEvent} event - The keyboard event
	*/
	handleKeyDown = (event) => {
		const { listbox } = this.refs;
		if (!(listbox instanceof Element)) return;
		const options = Array.from(listbox.querySelectorAll("[role=\"option\"]"));
		const currentFocused = options.find((option) => option instanceof HTMLElement && option.tabIndex === 0);
		let newFocusIndex = currentFocused ? options.indexOf(currentFocused) : 0;
		switch (event.key) {
			case "ArrowDown":
				event.preventDefault();
				newFocusIndex = Math.min(newFocusIndex + 1, options.length - 1);
				this.#moveFocus(options, newFocusIndex);
				break;
			case "ArrowUp":
				event.preventDefault();
				newFocusIndex = Math.max(newFocusIndex - 1, 0);
				this.#moveFocus(options, newFocusIndex);
				break;
			case "Enter":
			case " ":
				if (event.target instanceof Element) {
					const targetOption = event.target.closest("[role=\"option\"]");
					if (targetOption) {
						event.preventDefault();
						this.#selectOption(targetOption);
					}
				}
				break;
			case "Escape":
				event.preventDefault();
				this.#closeDropdown();
		}
	};
	/**
	* Handles details toggle event
	*/
	handleToggle = () => {
		const { details, summary, listbox } = this.refs;
		if (!(details instanceof HTMLDetailsElement) || !(summary instanceof HTMLElement)) return;
		const isOpen = details.open;
		summary.setAttribute("aria-expanded", isOpen.toString());
		if (isOpen && listbox instanceof Element) {
			const selectedOption = listbox.querySelector("[aria-selected=\"true\"]");
			if (selectedOption instanceof HTMLElement) selectedOption.focus();
		}
	};
	/**
	* Moves focus between options
	* @param {Element[]} options - The option elements
	* @param {number} newIndex - The index of the option to focus
	*/
	#moveFocus(options, newIndex) {
		options.forEach((option) => {
			if (option instanceof HTMLElement) option.tabIndex = -1;
		});
		const targetOption = options[newIndex];
		if (targetOption instanceof HTMLElement) {
			targetOption.tabIndex = 0;
			targetOption.focus();
		}
	}
	/**
	* Selects an option and triggers form submission
	* @param {Element} option - The option element to select
	*/
	#selectOption(option) {
		const input = option.querySelector("input[type=\"radio\"]");
		if (input instanceof HTMLInputElement && option instanceof HTMLElement) {
			this.querySelectorAll("[role=\"option\"]").forEach((opt) => {
				opt.setAttribute("aria-selected", "false");
			});
			option.setAttribute("aria-selected", "true");
			input.click();
			this.#closeDropdown();
		}
	}
	/**
	* Closes the dropdown and returns focus to summary
	*/
	#closeDropdown() {
		const { details, summary } = this.refs;
		if (details instanceof HTMLDetailsElement) {
			const options = this.querySelectorAll("[role=\"option\"]");
			const selectedOption = this.querySelector("[aria-selected=\"true\"]");
			options.forEach((opt) => {
				if (opt instanceof HTMLElement) opt.tabIndex = -1;
			});
			if (selectedOption instanceof HTMLElement) selectedOption.tabIndex = 0;
			details.open = false;
			if (summary instanceof HTMLElement) summary.focus();
		}
	}
	/**
	* Updates filter and sorting
	* @param {Event} event - The change event
	*/
	updateFilterAndSorting(event) {
		const section = this.closest(".shopify-section");
		const sectionId = section?.querySelector("facets-form-component[section-id]")?.getAttribute("section-id");
		const facetsForm = (sectionId ? findDesktopFacetsFormComponent(section, sectionId) : null) || this.closest("facets-form-component") || section?.querySelector("facets-form-component");
		if (!(facetsForm instanceof FacetsFormComponent)) return;
		const isMobile = window.innerWidth < 750;
		const shouldDisable = this.dataset.shouldUseSelectOnMobile === "true";
		if (shouldDisable) {
			if (isMobile) this.querySelectorAll("input[name=\"sort_by\"]").forEach((input) => {
				if (!(input instanceof HTMLInputElement)) return;
				input.disabled = true;
			});
			else {
				const selectElement = this.querySelector("select[name=\"sort_by\"]");
				if (!(selectElement instanceof HTMLSelectElement)) return;
				selectElement.disabled = true;
			}
		}
		facetsForm.updateFilters();
		this.updateFacetStatus(event);
		if (shouldDisable) {
			if (isMobile) this.querySelectorAll("input[name=\"sort_by\"]").forEach((input) => {
				if (!(input instanceof HTMLInputElement)) return;
				input.disabled = false;
			});
			else {
				const selectElement = this.querySelector("select[name=\"sort_by\"]");
				if (!(selectElement instanceof HTMLSelectElement)) return;
				selectElement.disabled = false;
			}
		}
		const { details } = this.refs;
		if (!(details instanceof HTMLDetailsElement)) return;
		details.open = false;
	}
	/**
	* Updates the facet status
	* @param {Event} event - The change event
	*/
	updateFacetStatus(event) {
		if (!(event.target instanceof HTMLSelectElement)) return;
		const details = this.querySelector("details");
		if (!details) return;
		const facetStatus = details.querySelector("facet-status-component");
		if (!(facetStatus instanceof FacetStatusComponent)) return;
		facetStatus.textContent = event.target.value !== details.dataset.defaultSortBy ? event.target.dataset.optionName ?? "" : "";
	}
};
if (!customElements.get("sorting-filter-component")) customElements.define("sorting-filter-component", SortingFilterComponent);
/**
* @typedef {Object} FacetStatusRefs
* @property {HTMLElement} facetStatus - The facet status element
*/
/**
* Handles facet status display
* @extends {Component<FacetStatusRefs>}
*/
var FacetStatusComponent = class extends Component {
	/**
	* Updates the list summary
	* @param {HTMLInputElement[]} checkedInputElements - The checked input elements
	*/
	updateListSummary(checkedInputElements) {
		const checkedInputElementsCount = checkedInputElements.length;
		this.getAttribute("facet-type") === "swatches" ? this.#updateSwatchSummary(checkedInputElements, checkedInputElementsCount) : this.#updateBubbleSummary(checkedInputElements, checkedInputElementsCount);
	}
	/**
	* Updates the swatch summary
	* @param {HTMLInputElement[]} checkedInputElements - The checked input elements
	* @param {number} checkedInputElementsCount - The number of checked inputs
	*/
	#updateSwatchSummary(checkedInputElements, checkedInputElementsCount) {
		const { facetStatus } = this.refs;
		facetStatus.classList.remove("facets__bubble");
		if (checkedInputElementsCount === 0) {
			facetStatus.innerHTML = "";
			return;
		}
		if (checkedInputElementsCount > 3) {
			facetStatus.innerHTML = checkedInputElementsCount.toString();
			facetStatus.classList.add("facets__bubble");
			return;
		}
		facetStatus.innerHTML = Array.from(checkedInputElements).map((inputElement) => {
			const swatch = inputElement.parentElement?.querySelector("span.swatch");
			const span = document.createElement("span");
			span.className = "visually-hidden";
			span.textContent = inputElement.getAttribute("aria-label") ?? "";
			return (swatch?.outerHTML ?? "") + span.outerHTML;
		}).join("");
	}
	/**
	* Updates the bubble summary
	* @param {HTMLInputElement[]} checkedInputElements - The checked input elements
	* @param {number} checkedInputElementsCount - The number of checked inputs
	*/
	#updateBubbleSummary(checkedInputElements, checkedInputElementsCount) {
		const { facetStatus } = this.refs;
		const filterStyle = this.dataset.filterStyle;
		facetStatus.classList.remove("facets__bubble");
		if (checkedInputElementsCount === 0) {
			facetStatus.innerHTML = "";
			return;
		}
		if (filterStyle === "horizontal" && checkedInputElementsCount === 1) {
			facetStatus.textContent = checkedInputElements[0]?.dataset.label ?? "";
			return;
		}
		facetStatus.innerHTML = checkedInputElementsCount.toString();
		facetStatus.classList.add("facets__bubble");
	}
	/**
	* Updates the price summary
	* @param {HTMLInputElement} minInput - The minimum price input
	* @param {HTMLInputElement} maxInput - The maximum price input
	*/
	updatePriceSummary(minInput, maxInput) {
		const minInputValue = minInput.value;
		const maxInputValue = maxInput.value;
		const { facetStatus } = this.refs;
		if (!minInputValue && !maxInputValue) {
			facetStatus.innerHTML = "";
			return;
		}
		const currency = facetStatus.dataset.currency || "";
		const minInputNum = this.#parseCents(minInputValue, "0", currency);
		const maxInputNum = this.#parseCents(maxInputValue, facetStatus.dataset.rangeMax, currency);
		facetStatus.innerHTML = `${this.#formatMoney(minInputNum)}–${this.#formatMoney(maxInputNum)}`;
	}
	/**
	* Parses a decimal number as minor units (cents for most currencies, but adjusted for zero-decimal currencies)
	* @param {string} value - The stringified decimal number to parse
	* @param {string} fallback - The fallback value in case `value` is invalid (formatted string like "11,400")
	* @param {string} currency - The currency code (e.g., 'USD', 'JPY', 'KRW')
	* @returns {number} The money value in minor units
	*/
	#parseCents(value, fallback = "0", currency = "") {
		const result = convertMoneyToMinorUnits(value, currency);
		if (result !== null) return result;
		const fallbackResult = convertMoneyToMinorUnits(fallback, currency);
		if (fallbackResult !== null) return fallbackResult;
		const cleanFallback = fallback.replace(/[^\d]/g, "");
		return parseInt(cleanFallback, 10) || 0;
	}
	/**
	* Formats money, replicated the implementation of the `money` liquid filters
	* @param {number} moneyValue - The money value
	* @returns {string} The formatted money value
	*/
	#formatMoney(moneyValue) {
		if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return "";
		const format = this.refs.moneyFormat.content.textContent || "{{amount}}";
		const currency = this.refs.facetStatus.dataset.currency || "";
		return formatMoney(moneyValue, format, currency);
	}
	/**
	* Clears the summary
	*/
	clearSummary() {
		this.refs.facetStatus.innerHTML = "";
	}
};
if (!customElements.get("facet-status-component")) customElements.define("facet-status-component", FacetStatusComponent);
//#endregion
